import { supabase } from './supabase'
import type { KitchenItem, KitchenTicket } from '../types'

/**
 * Mutfak ekranı fişleri (kitchen_tickets). Fişleri veritabanı tetiği yazıyor;
 * buradan yalnız okunuyor ve ürün durumları fonksiyonlarla değiştiriliyor.
 */

type Row = Record<string, unknown>

const ms = (v: unknown) => (typeof v === 'string' ? Date.parse(v) : undefined)

function toItem(v: unknown): KitchenItem {
  const r = (v ?? {}) as Row
  const st = r.status === 'cooking' || r.status === 'ready' ? r.status : 'new'
  return {
    key: String(r.key ?? ''),
    name: String(r.name ?? ''),
    qty: Number(r.qty ?? 0),
    note: typeof r.note === 'string' && r.note ? r.note : null,
    status: st,
    startedAt: ms(r.started_at),
    readyAt: ms(r.ready_at),
  }
}

function toTicket(r: Row): KitchenTicket {
  return {
    id: String(r.id),
    orderId: String(r.order_id),
    station: String(r.station ?? ''),
    label: String(r.label ?? ''),
    byName: String(r.by_name ?? ''),
    items: Array.isArray(r.items) ? r.items.map(toItem) : [],
    status: r.status === 'done' || r.status === 'void' ? r.status : 'open',
    createdAt: ms(r.created_at) ?? Date.now(),
    doneAt: ms(r.done_at),
    movedFrom: typeof r.moved_from === 'string' && r.moved_from ? r.moved_from : undefined,
    movedBy: typeof r.moved_by === 'string' && r.moved_by ? r.moved_by : undefined,
    movedAt: ms(r.moved_at),
    handedAt: ms(r.handed_at),
  }
}

/**
 * Bir adisyonun `since`ten sonra taşınmış açık fişleri (kasanın "MASA
 * DEĞİŞTİ" fişi için, 23 Eki 2026). Tek seferlik okuma.
 */
export async function fetchMovedTickets(
  tenantId: string,
  orderId: string,
  since: number,
): Promise<KitchenTicket[]> {
  if (!supabase) return []
  const { data, error } = await supabase
    .from('kitchen_tickets')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('order_id', orderId)
    .eq('status', 'open')
    .gte('moved_at', new Date(since).toISOString())
  if (error) throw new Error(error.message)
  return (data ?? []).map((r) => toTicket(r as Row))
}

/**
 * Son 24 saatin fişleri, canlı. Açıklar, kaldırılmamış iptaller ve son yarım
 * saatte gönderilenler ("Geri al" için). Garsonun "hazır" etiketi de bunu
 * kullanıyor.
 */
export function subscribeKitchen(tenantId: string, onData: (tickets: KitchenTicket[]) => void): () => void {
  if (!supabase) {
    onData([])
    return () => {}
  }
  let cancelled = false
  let timer: number | undefined

  const read = async () => {
    const since = new Date(Date.now() - 24 * 3600_000).toISOString()
    const { data, error } = await supabase!
      .from('kitchen_tickets')
      .select('*')
      .eq('tenant_id', tenantId)
      .gte('created_at', since)
      .order('created_at', { ascending: true })
    if (cancelled) return
    if (error) {
      console.error('[mutfak] okunamadı', error)
      return
    }
    onData((data ?? []).map((r) => toTicket(r as Row)))
  }
  // Art arda gelen değişiklikler (bir siparişin 3 tezgahı) tek okumada.
  const soon = () => {
    window.clearTimeout(timer)
    timer = window.setTimeout(() => void read(), 120)
  }

  void read()
  const channel = supabase
    .channel(`mutfak:${tenantId}:${crypto.randomUUID()}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'kitchen_tickets', filter: `tenant_id=eq.${tenantId}` },
      soon,
    )
    .subscribe()
  // Bağlantı koptuysa bile ekran bayatlamasın.
  const poll = window.setInterval(() => void read(), 30_000)

  return () => {
    cancelled = true
    window.clearTimeout(timer)
    window.clearInterval(poll)
    void supabase!.removeChannel(channel)
  }
}

async function rpc(name: string, args: Record<string, unknown>) {
  if (!supabase) throw new Error('Bağlantı yok.')
  const { error } = await supabase.rpc(name, args)
  if (error) throw new Error(error.message)
}

export const setKitchenItem = (ticketId: string, index: number, status: KitchenItem['status']) =>
  rpc('kitchen_set_item', { t: ticketId, idx: index, st: status })
export const finishKitchenTicket = (ticketId: string) => rpc('kitchen_ticket_done', { t: ticketId })
export const reopenKitchenTicket = (ticketId: string) => rpc('kitchen_ticket_reopen', { t: ticketId })
export const dismissKitchenTicket = (ticketId: string) => rpc('kitchen_ticket_dismiss', { t: ticketId })
/** Kiosk siparişi müşteriye verildi (kasada "Teslim edildi", supabase/kiosk-teslim.sql). */
export const handOverKiosk = (orderId: string) => rpc('kiosk_hand_over', { p_order: orderId })

/**
 * Mutfak ekranının tezgahları, canlı (kullanıcı, 11 Eki 2026): patron
 * Ayarlar > Mutfak ekranları'nda tezgahı değiştirince açık tablet yeniden
 * giriş yapmadan güncellensin. Hesabın kendi admins satırı dinleniyor;
 * bağlantı koparsa 30 saniyede bir ve ekrana dönülünce yeniden okunuyor.
 */
export function subscribeOwnStations(uid: string, cb: (stations: string[]) => void): () => void {
  if (!supabase) return () => {}
  const db = supabase
  let cancelled = false

  const toStations = (perms: unknown): string[] => {
    const p = (perms ?? {}) as { stations?: unknown; station?: unknown }
    if (Array.isArray(p.stations)) return p.stations.map(String).filter(Boolean)
    return typeof p.station === 'string' && p.station ? [p.station] : []
  }

  const read = async () => {
    const { data, error } = await db.from('admins').select('perms').eq('id', uid).maybeSingle()
    if (!cancelled && !error && data) cb(toStations(data.perms))
  }

  void read()
  const channel = db
    .channel(`ekran-tezgah:${uid}:${crypto.randomUUID()}`)
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'admins', filter: `id=eq.${uid}` }, (e) =>
      cb(toStations((e.new as Row).perms)),
    )
    .subscribe()
  const poll = window.setInterval(() => void read(), 30_000)
  const onVisible = () => document.visibilityState === 'visible' && void read()
  document.addEventListener('visibilitychange', onVisible)

  return () => {
    cancelled = true
    window.clearInterval(poll)
    document.removeEventListener('visibilitychange', onVisible)
    void db.removeChannel(channel)
  }
}

/**
 * Bir gün aralığının mutfak fişleri, bir kez (Gelişmiş Rapor: hazırlık
 * süresi ve darboğaz). Gün anahtarları yerel saatle sınır oluyor.
 */
export async function fetchKitchenRange(tenantId: string, from: string, to: string): Promise<KitchenTicket[]> {
  if (!supabase) return []
  const start = new Date(`${from}T00:00:00`).toISOString()
  const end = new Date(`${to}T23:59:59.999`).toISOString()
  const PAGE = 1000
  const out: KitchenTicket[] = []
  for (let i = 0; ; i += PAGE) {
    const { data, error } = await supabase
      .from('kitchen_tickets')
      .select('*')
      .eq('tenant_id', tenantId)
      .gte('created_at', start)
      .lte('created_at', end)
      .order('created_at', { ascending: true })
      .range(i, i + PAGE - 1)
    if (error) throw new Error(error.message)
    out.push(...(data ?? []).map(toTicket))
    if (!data || data.length < PAGE) break
  }
  return out
}
